<template>
  <div>
    <div
      class="hello"
      ref="chartdiv"
    />
    <!-- Composant mur de photos -->
    <PhotoWall 
      ref="images"
      :images="samples" 
      :title="title" 
    />

    <!-- Bouton retour à la carte -->
    <button
      v-if="samples.length"
      class="float-right fixed bottom-2 right-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full z-40 transition-all"
      @click="goToTop()"
    >
      {{ $t('backToMap') }}
    </button>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js';
import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchS3Json, listS3Objects } from '../utils/s3';
import PhotoWall from './PhotoWall.vue';


export default {
  components: { PhotoWall },
  name: 'Map',
  computed: {
      cities() {
          // Extraire toutes les villes (qui sont des objets, pas des tableaux)
          const allCities = [];
      this.countriesList.forEach(country => {
              if (country.cities) {
                  allCities.push(country.cities);
              }
          });
          return allCities;
      },
  },
  data() {
      return {
          images: [],
          samples: [],
          title: null,
          map: null,
          markers: [],
      countriesList: [],
      }
  },
  async mounted() {
  await this.loadCountries();

    // Configuration de Mapbox - remplacez YOUR_MAPBOX_ACCESS_TOKEN par votre token
    mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN || 'YOUR_MAPBOX_ACCESS_TOKEN';

    // Initialisation de la carte Mapbox avec projection globe et style dark
    this.map = new mapboxgl.Map({
      container: this.$refs.chartdiv,
      style: 'mapbox://styles/mapbox/dark-v11', // Style dark pour un rendu moderne
      center: [0, 20], // Centre de la carte [longitude, latitude]
      zoom: 1.5, // Zoom initial pour voir le globe entier
      projection: 'globe' // Projection globe pour un effet 3D
    });

    const that = this;

    this.map.on('load', function() {
      console.log('Carte Mapbox chargée');
      
      // Configuration spécifique au globe avec thème dark
      that.map.setFog({
        'range': [0.5, 10],
        'color': '#1a1a2e', // Couleur sombre pour l'atmosphère
        'horizon-blend': 0.2
      });
      
      // Personnaliser les couleurs pour le thème dark
      // Note: Ces propriétés peuvent ne pas être disponibles selon le style de base
      try {
        that.map.setPaintProperty('land', 'land-color', '#16213e');
        that.map.setPaintProperty('water', 'water-color', '#0f172a');
      } catch (e) {
        console.log('Impossible de modifier les couleurs de base:', e.message);
      }
      
      // Ajouter les pays visités comme couche
      that.addVisitedCountriesLayer();
      
      // Attendre un petit délai puis ajouter les marqueurs des villes
      setTimeout(() => {
        that.addCityMarkers();
      }, 500);
    });

    // Ajouter des contrôles de navigation adaptés au globe
    this.map.addControl(new mapboxgl.NavigationControl({
      visualizePitch: true,
      showZoom: true,
      showCompass: true
    }));

    // Permettre la rotation du globe avec la souris
    this.map.dragRotate.enable();
    this.map.touchZoomRotate.enableRotation();
  },

  methods: {
    async loadCountries() {
      const data = await fetchS3Json('countries.json', { countries: [] });
      this.countriesList = Array.isArray(data?.countries) ? data.countries : [];
    },
    addVisitedCountriesLayer() {
      // Obtenir les codes ISO des pays visités
      const visitedCountriesIds = this.countriesList.map(country => country.key);
      
      // Ajouter une source vectorielle avec les données naturelles de la terre
      this.map.addSource('countries', {
        type: 'vector',
        url: 'mapbox://mapbox.country-boundaries-v1'
      });

      // Couche pour tous les pays (couleur sombre par défaut)
      this.map.addLayer({
        id: 'country-fills',
        type: 'fill',
        source: 'countries',
        'source-layer': 'country_boundaries',
        paint: {
          'fill-color': process.env.VUE_APP_MAP_COUNTRY_COLOR,
          'fill-opacity': 1
        }
      });

      // Couche pour les pays visités (couleur spéciale avec gestion individuelle du survol)
      this.map.addLayer({
        id: 'visited-countries-fill',
        type: 'fill',
        source: 'countries',
        'source-layer': 'country_boundaries',
        filter: ['in', ['get', 'iso_3166_1'], ['literal', visitedCountriesIds]],
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            process.env.VUE_APP_MAP_COUNTRY_HOVER_COLOR, // Couleur de survol
            process.env.VUE_APP_MAP_COUNTRY_VISITED_COLOR // Couleur normale
          ],
          'fill-opacity': 1
        }
      });

      // Bordures des pays
      this.map.addLayer({
        id: 'country-borders',
        type: 'line',
        source: 'countries',
        'source-layer': 'country_boundaries',
        paint: {
          'line-color': '#374151', // Bordures grises sombres
          'line-width': 0.3,
          'line-opacity': 0.6
        }
      });

      let hoveredId = null;

      this.map.on('mousemove', 'visited-countries-fill', (e) => {
        if (e.features.length > 0) {
          if (hoveredId) {
            this.map.setFeatureState(
              { source: 'countries', sourceLayer: 'country_boundaries', id: hoveredId },
              { hover: false }
            );
          }
          hoveredId = e.features[0].id;
          this.map.setFeatureState(
            { source: 'countries', sourceLayer: 'country_boundaries', id: hoveredId },
            { hover: true }
          );
        }
      });

      this.map.on('mouseleave', 'visited-countries-fill', () => {
        if (hoveredId) {
          this.map.setFeatureState(
            { source: 'countries', sourceLayer: 'country_boundaries', id: hoveredId },
            { hover: false }
          );
        }
        hoveredId = null;
      });

      // Clic sur un pays visité pour zoomer
      this.map.on('click', 'visited-countries-fill', (e) => {
        if (e.features.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          
          // Calculer les limites du pays cliqué
          if (e.features[0].geometry.type === 'Polygon') {
            e.features[0].geometry.coordinates[0].forEach(coord => {
              bounds.extend(coord);
            });
          } else if (e.features[0].geometry.type === 'MultiPolygon') {
            e.features[0].geometry.coordinates.forEach(polygon => {
              polygon[0].forEach(coord => {
                bounds.extend(coord);
              });
            });
          }
          
          // Zoomer sur le pays
          this.map.fitBounds(bounds, { 
            padding: 50,
            duration: 1000 
          });
        }
      });
    },

    addCityMarkers() {
      let markersCreated = 0;
      
      console.log('Début de création des marqueurs des villes...');
      console.log('Données des pays:', this.countriesList.length, 'pays trouvés');

      // Parcourir toutes les villes et créer des marqueurs
      this.countriesList.forEach((country, countryIndex) => {
        console.log(`Pays ${countryIndex + 1}:`, country.key, 'ville:', country.cities ? country.cities.title : 'aucune');
        
        if (country.cities) {
          const city = country.cities; // cities est un objet, pas un tableau
          
          // Vérifier que la ville a des coordonnées valides
          if (city && typeof city.longitude === 'number' && typeof city.latitude === 'number') {
            console.log(`Création marqueur pour: ${city.title} (${city.longitude}, ${city.latitude})`);
            
            // Créer l'élément du marqueur avec une classe pour le contrôle de la taille
            const el = document.createElement('div');
            el.style.cursor = 'pointer';
            el.innerHTML = `<img
                class="block h-6 w-auto"
                src="${require('../assets/LV_2b_blanc.png')}"
                alt="LV"
              >`;

            // Ajouter les événements de clic et d'interaction
            el.addEventListener('click', () => {
              // Ne pas empêcher la propagation pour laisser la popup s'afficher
              this.onCityClick(city, el);
              marker.togglePopup();
            });

            el.addEventListener('mouseenter', () => {
              // Afficher manuellement la popup après un court délai
              setTimeout(() => {
                marker.togglePopup();
              }, 100);
            });

            el.addEventListener('mouseleave', () => {
              marker.togglePopup();
            });

            // Créer le marqueur Mapbox avec l'élément personnalisé
            const marker = new mapboxgl.Marker(el, { anchor: 'bottom' })
              .setLngLat([city.longitude, city.latitude])
              .setPopup(new mapboxgl.Popup({ 
                offset: [0, -30],
                closeButton: false,
                closeOnClick: false,
                className: 'modern-popup'
              })
                .setHTML(`
                  <div style="background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); border-radius: 12px; padding: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.6); border: 1px solid #374151; min-width: 200px; backdrop-filter: blur(10px);">
                    <div style="display: flex; align-items: center; justify-content: center; gap: 12px;">
                      <h3 style="color: white; font-weight: bold; font-size: 18px; margin: 0; letter-spacing: 0.5px; text-align: center;">${city.title}</h3>
                    </div>
                  </div>
                `))
              .addTo(this.map);

            // Stocker la référence du marqueur avec son élément pour la gestion du zoom
            this.markers.push({ marker, element: el, city });
            markersCreated++;
          } else {
            console.warn(`Ville ignorée (coordonnées invalides):`, city);
          }
        }
      });
      
      console.log(`Total de ${markersCreated} marqueurs créés sur la carte`);
      
      if (markersCreated === 0) {
        console.error('Aucun marqueur créé ! Vérifiez la structure des données cities');
        console.log('Structure attendue: { longitude: number, latitude: number, title: string }');
      }
    },

    async onCityClick(city, markerContainer) {
      console.log(`Clic sur la ville: ${city.title}`);
      
      // Animation de pulse sur le marqueur
      markerContainer.style.animation = 'marker-pulse 0.6s ease-in-out';
      setTimeout(() => {
        markerContainer.style.animation = '';
      }, 600);

      // Centrer la carte sur la ville avec un zoom plus proche et animation fluide
      this.map.flyTo({
        center: [city.longitude, city.latitude],
        zoom: 12, // Zoom plus proche pour voir les détails
        pitch: 45, // Légère inclinaison pour un effet 3D
        bearing: 0, // Réinitialiser la rotation
        duration: 1800, // Animation plus longue pour un effet spectaculaire
        essential: true // Animation prioritaire
      });

      // Afficher un message de chargement temporaire
      console.log(`Chargement des photos de ${city.title}...`);

      // Charger les images avec gestion d'erreur
      try {
        const images = await listS3Objects(`images/${city.title}/`);
        
        console.log(`${images.length} photos trouvées pour ${city.title}`);
        
        this.title = city.title;
        this.samples = images;
        
        // Scroll vers la section des images avec un délai pour l'animation de la carte
        setTimeout(() => {
          this.goTo('images');
        }, 500);
        
      } catch (error) {
        console.error(`Erreur lors du chargement des images pour ${city.title}:`, error);
        // Afficher un message d'erreur à l'utilisateur si nécessaire
        this.title = city.title;
        this.samples = [];
      }
    },

    openModal(image, index) {
      this.selectedImage = image;
      this.selectedImageIndex = index;
      document.body.style.overflow = 'hidden'; // Empêcher le scroll
    },

    closeModal() {
      this.selectedImage = null;
      this.selectedImageIndex = -1;
      document.body.style.overflow = 'auto'; // Restaurer le scroll
    },

    previousImage() {
      if (this.selectedImageIndex > 0) {
        this.selectedImageIndex--;
        this.selectedImage = this.samples[this.selectedImageIndex];
      }
    },

    nextImage() {
      if (this.selectedImageIndex < this.samples.length - 1) {
        this.selectedImageIndex++;
        this.selectedImage = this.samples[this.selectedImageIndex];
      }
    },

    handleKeydown(event) {
      if (this.selectedImage) {
        switch (event.key) {
          case 'Escape':
            this.closeModal();
            break;
          case 'ArrowLeft':
            this.previousImage();
            break;
          case 'ArrowRight':
            this.nextImage();
            break;
        }
      }
    },
    
    goToTop() {
      this.$refs['chartdiv'].scrollIntoView({ behavior: "smooth" });
      this.samples = [];
      this.title = null;
      

    },
    
    goTo(ref) {
      if (ref === 'images' && this.$refs[ref]) {
        // Pour le composant PhotoWall, on fait défiler vers l'élément racine du composant
        this.$refs[ref].$el.scrollIntoView({ behavior: "smooth" });
      } else if (this.$refs[ref]) {
        this.$refs[ref].scrollIntoView({ behavior: "smooth" });
      }
    }
  },

  beforeUnmount() {
    // Nettoyer les événements clavier
    document.removeEventListener('keydown', this.handleKeydown);
    
    // Restaurer le scroll si modal ouvert
    if (this.selectedImage) {
      document.body.style.overflow = 'auto';
    }
    
    // Nettoyer les événements d'écoute du zoom
    if (this.map) {
      this.map.off('zoom');
      this.map.off('zoomend');
    }
    
    // Nettoyer tous les marqueurs
    if (this.markers && this.markers.length > 0) {
      this.markers.forEach(markerData => {
        if (markerData.marker) {
          markerData.marker.remove();
        }
      });
      this.markers = [];
    }
    
    // Supprimer la carte
    if (this.map) {
      this.map.remove();
    }
  }
}
</script>

<style scoped>
.hello {
  width: 100%;
  height: 500px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.6);
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #374151 100%);
  border: 1px solid #374151;
}

/* Styles pour la popup moderne */
:deep(.modern-popup .mapboxgl-popup-content) {
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

:deep(.modern-popup .mapboxgl-popup-tip) {
  border-top-color: #1e293b !important;
}

/* Animation pour les marqueurs */
@keyframes marker-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>